/**
 * Knowledge Base (RAG) admin API client (ADR-0035) — a data-plane entity sibling of
 * datasets. A KB binds one `embedding` LLM config; documents are uploaded + ingested
 * (extract → chunk → embed) and agents retrieve passages from the PUBLISHED ones.
 */
import { api } from "./api";

export interface KbDocument {
  id: string;
  filename: string;
  content_type: string | null;
  byte_size: number;
  /** `pending` | `processing` | `ready` | `failed`. */
  status: string;
  error: string | null;
  chunk_count: number;
  created_at: string;
  updated_at: string | null;
}

export interface KnowledgeBase {
  id: string;
  name: string;
  description: string | null;
  embedding_config_id: string;
  /** The vector dimension (set on first ingest); null until a document is added. */
  dimension: number | null;
  published: boolean;
  tags: string[];
  document_count: number;
  chunk_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface KnowledgeBaseDetail extends KnowledgeBase {
  documents: KbDocument[];
}

export interface KnowledgeBaseCreate {
  name: string;
  description?: string | null;
  embedding_config_id: string;
  tags?: string[];
  published?: boolean;
}

export interface KnowledgeBaseUpdate {
  name?: string;
  description?: string | null;
  tags?: string[];
  published?: boolean;
}

/** A passage returned by the operator test-search (internal vector search engine). */
export interface KbPassage {
  source: string;
  ordinal: number;
  score: number;
  text: string;
}

export interface KbSearchResult {
  passages: KbPassage[];
  /**
   * `null` on a clean hit set; otherwise a stable code the UI maps to a friendly
   * message: `not_ready` | `no_matches` | `empty_query` | `embedding_config_not_found`
   * | `embed_failed:<detail>`.
   */
  note: string | null;
}

export const knowledgeBasesApi = {
  list: (params?: { tag?: string[] }) =>
    api.get<KnowledgeBase[]>("/knowledge-bases", { query: params }),
  get: (id: string) => api.get<KnowledgeBaseDetail>(`/knowledge-bases/${id}`),
  create: (body: KnowledgeBaseCreate) =>
    api.post<KnowledgeBaseDetail>("/knowledge-bases", body),
  update: (id: string, body: KnowledgeBaseUpdate) =>
    api.patch<KnowledgeBaseDetail>(`/knowledge-bases/${id}`, body),
  remove: (id: string) => api.delete<void>(`/knowledge-bases/${id}`),
  /** Upload one or more documents → synchronous ingest; returns the created docs. */
  uploadDocuments: (id: string, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    return api.upload<KbDocument[]>(`/knowledge-bases/${id}/documents`, fd);
  },
  deleteDocument: (id: string, documentId: string) =>
    api.delete<void>(`/knowledge-bases/${id}/documents/${documentId}`),
  /** Internal vector-search probe — test retrieval on a KB (incl. drafts). */
  search: (id: string, query: string, limit = 8) =>
    api.post<KbSearchResult>(`/knowledge-bases/${id}/search`, { query, limit }),
};
